import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Phone, Mail, FileText, Link2, Clock,
  User, Building2, Tag, Loader2, MessageSquare, Folder,
  PhoneIncoming, PhoneOutgoing, MapPin, Pencil, StickyNote, Briefcase
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { API } from "../config/api";

const ACTIVITY_ICONS = {
  attendance_note: Phone,
  telephone_message: MessageSquare,
  matter_link: Link2,
  email_filed: Mail,
};

const ACTIVITY_COLORS = {
  attendance_note: "text-blue-400 bg-blue-900/30 border-blue-800/40",
  telephone_message: "text-status-amber bg-status-amber-dim/30 border-amber-800/40",
  matter_link: "text-teal bg-teal-dim/30 border-teal/40",
  email_filed: "text-purple-400 bg-purple-900/30 border-purple-800/40",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// BM-H3408 (1 Oct 2026): the page showed a name and nothing else. Blank
// fields were hidden, so a contact with nothing captured looked broken and
// staff had no way to fill it in. Every field now shows, blanks read
// "Not recorded", linked matters are listed, and staff can edit.
const EDIT_FIELDS = [
  "display_name", "organisation", "primary_email", "primary_phone",
  "other_emails", "other_phones", "address", "notes",
];

function toForm(contact) {
  const f = {};
  EDIT_FIELDS.forEach((k) => {
    const v = contact?.[k];
    f[k] = Array.isArray(v) ? v.join(", ") : (v || "");
  });
  return f;
}

function splitList(s) {
  return (s || "").split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
}

function DetailRow({ icon: Icon, label, value, testId }) {
  const empty = !value || (Array.isArray(value) && value.length === 0);
  return (
    <div className="flex items-start gap-3 py-2" data-testid={testId}>
      <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
        {empty ? (
          <div className="text-sm text-slate-600 italic">Not recorded</div>
        ) : (
          <div className="text-sm text-white whitespace-pre-line break-words">
            {Array.isArray(value) ? value.join(", ") : value}
          </div>
        )}
      </div>
    </div>
  );
}

// BM-H3512: conflict_index_sync statuses that mean the SharePoint Conflict
// Index does not hold this contact's latest name, address or email.
const SYNC_PROBLEM_STATUSES = ["failed", "partial", "skipped"];

function roleLabel(role) {
  return (role || "").replace(/_/g, " ");
}

export default function ContactDetailPage() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(toForm(null));
  const [saving, setSaving] = useState(false);

  const loadContact = useCallback(async () => {
    setLoading(true);
    try {
      const [contactResp, activityResp] = await Promise.all([
        axios.get(`${API}/contacts/${contactId}`, { withCredentials: true }),
        axios.get(`${API}/contacts/${contactId}/activity`, { withCredentials: true }),
      ]);
      setContact(contactResp.data);
      setActivity(activityResp.data);
    } catch (e) {
      console.error("Failed to load contact:", e);
    }
    setLoading(false);
  }, [contactId]);

  useEffect(() => { loadContact(); }, [loadContact]);

  const startEdit = () => { setForm(toForm(contact)); setEditing(true); };

  const save = async () => {
    if (!form.display_name.trim()) { toast.error("Name cannot be blank"); return; }
    setSaving(true);
    const body = {};
    EDIT_FIELDS.forEach((k) => {
      body[k] = (k === "other_emails" || k === "other_phones") ? splitList(form[k]) : form[k];
    });
    try {
      const patched = await axios.patch(`${API}/contacts/${contactId}`, body, { withCredentials: true });
      // Read the contact back rather than trusting what we sent.
      const resp = await axios.get(`${API}/contacts/${contactId}`, { withCredentials: true });
      setContact(resp.data);
      setEditing(false);
      toast.success("Contact saved");
      // BM-H3512: the edit is saved either way; say so when the Conflict
      // Index was not updated with it.
      const sync = patched.data?.conflict_index_sync;
      if (sync && SYNC_PROBLEM_STATUSES.includes(sync.status)) {
        toast.warning("Saved, but the Conflict Index was not fully updated. See the notice on this page.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the contact");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="min-h-screen bg-navy flex flex-col items-center justify-center gap-4">
        <p className="text-slate-400">Contact not found</p>
        <Button onClick={() => navigate(-1)} variant="outline" className="border-slate-700 text-slate-300">Go back</Button>
      </div>
    );
  }

  const activities = activity?.activities || [];

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="contact-detail-page">
      {/* Header */}
      <div className="border-b border-navy-border bg-navy">
        <div className="max-w-4xl mx-auto px-6 py-5">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-teal mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />Back
          </button>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal to-teal-dim flex items-center justify-center text-xl font-bold text-white shrink-0">
              {(contact.display_name || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-white truncate" data-testid="contact-name">{contact.display_name}</h1>
              {contact.organisation && (
                <div className="flex items-center gap-1.5 mt-1 text-sm text-slate-400">
                  <Building2 className="w-3.5 h-3.5" />{contact.organisation}
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-2">
                {contact.primary_phone && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Phone className="w-3 h-3" />{contact.primary_phone}
                  </div>
                )}
                {contact.primary_email && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Mail className="w-3 h-3" />{contact.primary_email}
                  </div>
                )}
              </div>
              {contact.labels?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {contact.labels.map((label, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-teal-dim/30 text-teal border border-teal/40">
                      <Tag className="w-2.5 h-2.5" />{label.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* BM-H3512: the last edit did not fully reach the Conflict Index */}
      {contact.conflict_index_sync && SYNC_PROBLEM_STATUSES.includes(contact.conflict_index_sync.status) && (
        <div className="max-w-4xl mx-auto px-6 pt-6">
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="contact-conflict-index-sync-notice">
            <p className="font-medium">
              {contact.conflict_index_sync.status === "skipped"
                ? "The last edit to this contact was saved but not sent to the SharePoint Conflict Index."
                : "The last edit to this contact was saved but did not fully reach the SharePoint Conflict Index."}
              {" "}A conflict search may still show the old details. Please tell the system administrator.
            </p>
            {contact.conflict_index_sync.error && (
              <p className="mt-1 text-xs text-amber-300/80 break-words">{contact.conflict_index_sync.error}</p>
            )}
          </div>
        </div>
      )}

      {/* Details */}
      <div className="max-w-4xl mx-auto px-6 pt-6">
        <div className="rounded-lg border border-navy-border p-4" data-testid="contact-details">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-white">Contact details</h2>
            {!editing && (
              <Button size="sm" variant="outline" onClick={startEdit} className="border-slate-700 text-slate-300" data-testid="contact-edit">
                <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit
              </Button>
            )}
          </div>
          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="contact-edit-form">
              {[
                ["display_name", "Name"],
                ["organisation", "Organisation"],
                ["primary_email", "Email"],
                ["primary_phone", "Phone"],
                ["other_emails", "Other emails (comma separated)"],
                ["other_phones", "Other phones (comma separated)"],
              ].map(([k, label]) => (
                <label key={k} className="text-xs text-slate-400">
                  {label}
                  <Input
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1"
                    data-testid={`contact-input-${k}`}
                  />
                </label>
              ))}
              <label className="text-xs text-slate-400 sm:col-span-2">
                Address
                <Textarea value={form.address} rows={2} className="mt-1"
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="contact-input-address" />
              </label>
              <label className="text-xs text-slate-400 sm:col-span-2">
                Notes
                <Textarea value={form.notes} rows={3} className="mt-1"
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="contact-input-notes" />
              </label>
              <div className="sm:col-span-2 flex justify-end gap-2">
                <Button size="sm" variant="outline" className="border-slate-700 text-slate-300" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="contact-save">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <DetailRow icon={Building2} label="Organisation" value={contact.organisation} testId="detail-organisation" />
              <DetailRow icon={Mail} label="Email" value={contact.primary_email} testId="detail-email" />
              <DetailRow icon={Phone} label="Phone" value={contact.primary_phone} testId="detail-phone" />
              <DetailRow icon={Mail} label="Other emails" value={contact.other_emails} testId="detail-other-emails" />
              <DetailRow icon={Phone} label="Other phones" value={contact.other_phones} testId="detail-other-phones" />
              <DetailRow icon={MapPin} label="Address" value={contact.address} testId="detail-address" />
              <div className="sm:col-span-2">
                <DetailRow icon={StickyNote} label="Notes" value={contact.notes} testId="detail-notes" />
              </div>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-navy-border p-4 mt-4" data-testid="contact-linked-matters">
          <h2 className="text-base font-semibold text-white mb-2">Linked matters</h2>
          {(contact.linked_matters || []).length === 0 ? (
            <p className="text-sm text-slate-600 italic">Not linked to any matter</p>
          ) : (
            <ul className="divide-y divide-navy-border">
              {contact.linked_matters.map((m) => (
                <li key={`${m.matter_ref}-${m.role}`} className="py-2 flex items-center gap-3">
                  <Briefcase className="w-4 h-4 text-slate-500 shrink-0" />
                  <button
                    onClick={() => navigate(`/matter/${encodeURIComponent(m.matter_ref)}`)}
                    className="font-mono text-xs text-teal hover:underline"
                  >
                    {m.matter_ref}
                  </button>
                  <span className="text-sm text-slate-300 truncate">{m.matter_title || m.client_name}</span>
                  {m.role && <span className="ml-auto text-[11px] text-slate-500">{roleLabel(m.role)}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white">Activity Timeline</h2>
          <span className="text-xs text-slate-500">{activities.length} activities</span>
        </div>

        {activities.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No activity recorded yet</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-5 top-0 bottom-0 w-px bg-navy-hover" />

            <div className="space-y-4">
              {activities.map((item, i) => {
                const Icon = ACTIVITY_ICONS[item.activity_type] || FileText;
                const colorClass = ACTIVITY_COLORS[item.activity_type] || "text-slate-400 bg-slate-900/30 border-slate-800/40";
                return (
                  <div key={i} className="relative pl-12" data-testid={`activity-item-${i}`}>
                    {/* Icon circle */}
                    <div className={`absolute left-2.5 w-5 h-5 rounded-full border flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-2.5 h-2.5" />
                    </div>
                    {/* Card */}
                    <div className="p-3.5 rounded-lg border border-navy-border bg-navy hover:border-navy-border transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white">{item.summary}</p>
                          {item.matter_ref && (
                            <span className="inline-block mt-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-navy text-teal border border-teal/40">
                              {item.matter_ref}
                            </span>
                          )}
                          {item.note_preview && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{item.note_preview}</p>}
                          {item.message_preview && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{item.message_preview}</p>}
                          {item.activity_type === "attendance_note" && (
                            <div className="flex items-center gap-3 mt-1.5">
                              {item.direction && (
                                <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                  {item.direction === "inbound" ? <PhoneIncoming className="w-2.5 h-2.5" /> : <PhoneOutgoing className="w-2.5 h-2.5" />}
                                  {item.direction}
                                </span>
                              )}
                              {item.channel && <span className="text-[10px] text-slate-600">{item.channel}</span>}
                              {item.duration_minutes && <span className="text-[10px] text-slate-600">{item.duration_minutes}min</span>}
                            </div>
                          )}
                          {item.activity_type === "email_filed" && item.from && (
                            <p className="text-[10px] text-slate-600 mt-1">from {item.from}</p>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-600 whitespace-nowrap shrink-0">{formatDate(item.date)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
